// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// hooks
import { useGoBack } from 'hooks/useGoBack';

// utils
import { formatRoleName } from '@karehero/llama/utils/format';

// models
import { FieldType } from '@karehero/models';

// components
import { NewAccountPage } from '@karehero/llama/pwa/pages/NewAccountPage/NewAccountPage';

// editor
import { createFieldHelper } from '@karehero/llama/components/organisms/EditorFields/fieldHelper';

// store
import { Account } from '@karehero/models';
import { useCreateAccountMutation } from 'store/api/account';
import { useGetAllAccountRoleQuery } from 'store/api/accountRole';
import { useGetAllOrganizationQuery } from 'store/api/organization';
import { useGetAllReferringPartnersWithOrganizationsQuery } from 'store/api/referringPartner';

/**
 * NewCareCircle is the dashboard page.
 */
export const NewCareCircle = () => {
  // state
  const [account, setAccount] = useState<Partial<Account>>({});

  // hooks
  const goBack = useGoBack();
  const [createAccount] = useCreateAccountMutation();
  const { data: accountRoles, isLoading: isAccountRolesLoading } =
    useGetAllAccountRoleQuery();
  // TODO[pagination]: replace with searchable autocomplete
  const { data: organizationsData } = useGetAllOrganizationQuery({
    perPage: 1000,
  });
  const organizations = organizationsData?.data;
  const { data: partnersWithOrgs } =
    useGetAllReferringPartnersWithOrganizationsQuery();

  useEffect(() => {
    if (isAccountRolesLoading) return;

    const defaultRole = accountRoles?.find(
      (accountRole) => accountRole.name === 'Organization Owner',
    );

    if (!defaultRole) return;

    setAccount((prev) => ({ ...prev, accountRoleID: defaultRole.id }));
  }, [isAccountRolesLoading, accountRoles]);

  const selectedRole = useMemo(() => {
    if (!account.accountRoleID) return undefined;
    return (accountRoles || []).find((r) => r.id === account.accountRoleID);
  }, [account.accountRoleID, accountRoles]);

  const isPartnerAdmin = selectedRole?.name === 'Referring Partner Admin';

  const fields = useMemo(() => {
    const base: Record<string, any> = {
      firstName: {
        label: 'First Name',
        field: {
          type: FieldType.Text,
        },
      },
      lastName: {
        label: 'Last Name',
        field: {
          type: FieldType.Text,
        },
      },
      email: {
        label: 'Email',
        field: {
          type: FieldType.Text,
        },
      },
      accountRoleID: {
        label: 'Account Role',
        isRequired: true,
        field: {
          type: FieldType.Select,
          options: (accountRoles || [])
            .filter((accountRole) => accountRole.name !== 'Member')
            .filter((accountRole) => accountRole.name !== 'Care Concierge')
            .map((accountRole) => ({
              label: formatRoleName(accountRole.name),
              value: accountRole.id,
            })),
        },
      },
    };

    if (isPartnerAdmin) {
      base.partnerId = {
        label: 'Partner',
        isRequired: true,
        field: {
          type: FieldType.Select,
          options: (partnersWithOrgs || []).map((p) => ({
            label: p.partner.name,
            value: p.partner.id,
          })),
        },
      };
    } else {
      base.organizationID = {
        label: 'Organisation',
        isRequired: true,
        field: {
          type: FieldType.Select,
          options: (organizations || []).map((organization) => ({
            label: organization.name,
            value: organization.id,
          })),
        },
      };
    }

    return createFieldHelper<Account>(base);
  }, [organizations, accountRoles, partnersWithOrgs, isPartnerAdmin]);

  // methods
  const handleSave = useCallback(async () => {
    const res = await createAccount(account as Account);
    if (!('data' in res)) {
      return;
    }

    goBack();
    return;
  }, [goBack, createAccount, account]);

  return (
    <NewAccountPage
      fields={fields}
      value={account}
      onChange={setAccount}
      onBack={goBack}
      onSave={handleSave}
    />
  );
};

export default NewCareCircle;
